import React, { useContext, useState } from "react";
import "./ExposuresAnalysis.css";

export default function ExposuresAnalysis(props) {
  const [showBreakdown, setShowBreakdown] = useState(false);

  let posBreakdowns = {};
  let nonFlexPositionCount = 0;
  let flexablePositionCount = 0;

  let subOptimalPosition = false;

  for (const [pos, posData] of Object.entries(props.positions)) {
    nonFlexPositionCount += posData.amount;

    if (props.flexablePositions.includes(pos)) {
      flexablePositionCount += posData.amount;
    }

    const players = props.players.filter(
      (player) =>
        player.slatePlayer.rosterSlots.edges
          .map((edge) => {
            return edge.node.name;
          })
          .some((item) => {
            return item === pos;
          }) &&
        (player.exposure > 0 || player.id in props.sessionExposureChanges)
    );

    if (!(pos in posBreakdowns))
      posBreakdowns[pos] = { exposureSalary: 0, count: 0 };

    players.map((player) => {
      let currentExposure =
        player.id in props.sessionExposureChanges
          ? props.sessionExposureChanges[player.id]
          : player.exposure;

      if (isNaN(currentExposure)) {
        currentExposure = 0;
      }

      posBreakdowns[pos]["exposureSalary"] +=
        player.slatePlayer.salary * currentExposure;

      if (currentExposure > 0) {
        posBreakdowns[pos]["count"] += currentExposure;
      }
    }, 0);
  }

  flexablePositionCount += props.numFlexSlots;

  const flexablePositionTarget = flexablePositionCount * props.numLineups;

  let currentExposureForFlexablePositions = 0;

  const positionAnalysisElements = Object.keys(posBreakdowns).map((key) => {
    if (props.flexablePositions.includes(key)) {
      currentExposureForFlexablePositions += posBreakdowns[key]["count"];
    }

    const targetSelected = props.positions[key].amount * props.numLineups;

    let selectedCheck;

    if (props.flexablePositions.includes(key)) {
      if (props.selectedSportName === "CFB" && key === "QB") {
        selectedCheck =
          posBreakdowns[key]["count"] >= targetSelected &&
          posBreakdowns[key]["count"] <= targetSelected * 2;
      } else {
        selectedCheck = posBreakdowns[key]["count"] >= targetSelected;
      }
    } else {
      selectedCheck = posBreakdowns[key]["count"] === targetSelected;
    }

    if (!selectedCheck) {
      subOptimalPosition = true;
    }

    return (
      <div
        key={key}
        className={
          "exposures-analysis-position " +
          (selectedCheck ? "gm-good-text" : "gm-bad-text")
        }
      >
        <div>
          <strong>{key}</strong>
        </div>
        <div>
          $
          {posBreakdowns[key]["count"] > 0
            ? Math.round(
                posBreakdowns[key]["exposureSalary"] /
                  posBreakdowns[key]["count"]
              )
            : 0}{" "}
          / player
        </div>
        <div className={selectedCheck ? "gm-good-text" : "gm-bad-text"}>
          {posBreakdowns[key]["count"]} players (
          {props.selectedSportName === "CFB" && key === "QB"
            ? `Min ${targetSelected}, Max ${targetSelected * 2}`
            : props.flexablePositions.includes(key)
            ? `Min Target ${targetSelected}`
            : `Target ${targetSelected}`}
          )
        </div>
      </div>
    );
  });

  return (
    <div className="exposures-analysis">
      <div className="exposures-analysis-header">
        <p
          className={
            props.totalExposure === props.numLineups * props.playersPerLineup
              ? "gm-good-text"
              : "gm-bad-text"
          }
        >
          <strong>TOTAL EXPOSURE: </strong> {props.totalExposure}
          <br />
          {props.totalExposure !== props.numLineups * props.playersPerLineup
            ? `(There are ${
                props.numLineups * props.playersPerLineup
              } roster slots in ${
                props.numLineups
              } lineups. The Green Machine will still run if the total exposure does not equal ${
                props.numLineups * props.playersPerLineup
              }, but a larger or smaller number may cause your final exposures to be off.`
            : null}
        </p>
        <p
          className={
            props.totalExposureSalary >= props.minExposureSalary &&
            props.totalExposureSalary <= props.maxExposureSalary
              ? "gm-good-text"
              : "gm-bad-text"
          }
        >
          <strong>TOTAL SALARY * EXPOSURE: </strong>
          {" $"}
          {Number(props.totalExposureSalary).toLocaleString()}
          <br />
          {props.totalExposureSalary < props.minExposureSalary ||
          props.totalExposureSalary > props.maxExposureSalary
            ? `Based on your config, this number should be between $${Number(
                props.minExposureSalary
              ).toLocaleString()} and $${Number(
                props.maxExposureSalary
              ).toLocaleString()} for optimal performance. ` +
              (props.totalExposureSalary > props.maxExposureSalary
                ? `You are over by $${
                    props.totalExposureSalary - props.maxExposureSalary
                  }`
                : `You are under by $${
                    props.minExposureSalary - props.totalExposureSalary
                  }`)
            : null}
        </p>
        <p
          className={
            currentExposureForFlexablePositions === flexablePositionTarget
              ? "gm-good-text"
              : "gm-bad-text"
          }
        >
          <strong>FLEX ELIGIBLE EXPOSURE: </strong>{" "}
          {currentExposureForFlexablePositions}
          <br />
          {currentExposureForFlexablePositions !== flexablePositionTarget
            ? `(You should have ${flexablePositionTarget} FLEX eligible players. The Green Machine will still run if the total FLEX eligible exposure does not equal ${flexablePositionTarget}, but a larger or smaller number may cause your final exposures to be off.`
            : null}
        </p>
        <p>
          <button
            onClick={(e) => {
              e.preventDefault();
              setShowBreakdown(!showBreakdown);
            }}
          >
            {showBreakdown ? "Hide" : "Show"} Breakdown
          </button>{" "}
          {subOptimalPosition ? (
            <span className="gm-bad-text">
              One or more positions is suboptimal.
            </span>
          ) : (
            <span className="gm-good-text">All positions are optimal.</span>
          )}
        </p>
      </div>
      <div
        className="exposures-analysis-breakdown"
        style={showBreakdown ? {} : { display: "none" }}
      >
        {positionAnalysisElements}
      </div>
    </div>
  );
}
